import { factory } from '../../../../factory-api/factory'
import {
  EscoLanguage,
  JobMatchDateFilter,
  JobAdvertSortOrder,
  type AdvertCountResult,
  type JobMatchGeoSettings,
  type JobMatchProfile,
  type JobMatchSearchFilters,
  type JobResultItem,
  type TranslatedSearchFilter,
  type JobMatchNotificationSettings
} from '../../../../generated-api'
import { defineStore } from 'pinia'
import {
  useLocationStore,
  useUserStore,
  useAppStore,
  useOccupationStore,
  useWizardStore
} from '../..'
import router from '../../../router'
import { useLocalizationStore } from '../..'
import {
  FilterItemType,
  type IFilterItem,
  type IFilterOption
} from '../../../interfaces/IFilterItem'
import { getLocaleBasedOnDeviceLocale, sortArrayAlphabetically } from '../../../application/helpers'
import type { ITab } from '../../../interfaces/ITab'
import { getLocalStorageTab, setLocalStorageTab } from '../../../application/helpers/utilities'

export const useJobmatchStore = defineStore('jobmatchStore', () => {
  const locationStore = useLocationStore()
  const localizationStore = useLocalizationStore()
  const userStore = useUserStore()
  const appStore = useAppStore()
  const occupationStore = useOccupationStore()
  const wizardStore = useWizardStore()

  const jobmatchProfile = ref<JobMatchProfile>()
  const geoSettings = ref<JobMatchGeoSettings>()
  const notificationSettings = ref<JobMatchNotificationSettings>()

  const jobPostings = ref<JobResultItem[]>([])
  const savedJobPostings = ref<JobResultItem[]>([])
  const selectedJobPosting = ref<JobResultItem | undefined>(undefined)
  const totalResults = ref<number>(0)
  const jobmatchCount = ref<AdvertCountResult>()
  const blockItemJobsFound = ref<number>(0)
  const jobFilters = ref<IFilterItem[]>([])

  const isSearching = ref<boolean>(false)
  const isFetching = ref<boolean>(false)
  const initialized = ref<boolean>(false)

  const currentJobDescription = ref<string>('')

  const jobmatchParams = ref<JobMatchSearchFilters>({
    order: JobAdvertSortOrder.DateDescending,
    limit: 50,
    offset: 0
  })

  const gettingFilters = ref<boolean>(false)

  const today = new Date()
  const dates = [
    {
      id: JobMatchDateFilter.Last24Hours,
      translationKey: 'general_24_Hours',
      date: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1).toString()
    },
    {
      id: JobMatchDateFilter.LastWeek,
      translationKey: 'general_One_Week',
      date: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7).toString()
    },
    {
      id: JobMatchDateFilter.LastTwoWeeks,
      translationKey: 'general_Two_Weeks',
      date: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 14).toString()
    },
    {
      id: JobMatchDateFilter.LastMonth,
      translationKey: 'general_One_Month',
      date: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30).toString()
    }
  ]

  const tabs = ref<ITab[]>([])
  const activeTab = ref<ITab>()

  const init = async () => {
    await getJobmatchProfile()
    await getJobMatchCount()

    const country =
      jobmatchProfile.value?.geoSettings?.country ??
      localStorage.getItem('appLanguageLocale')?.split('-')[1] ??
      userStore.member.countryCode ??
      'DK'

    await locationStore.getTypeaheadByCountry(country)

    if (geoSettings.value !== undefined) {
      const calculatedRadius =
        geoSettings.value.radiusKm === 0
          ? locationStore.radiusValues[2].id
          : Number(geoSettings.value.radiusKm)

      locationStore.setSelectedRadius(calculatedRadius)
    }

    locationStore.setSelectedCountry({
      id: country ?? 'DK',
      name: country ?? 'DK'
    })

    getPositionAndLocation()

    initialized.value = true
  }

  const resultPageInit = async () => {
    await getJobFilters()
    await getSavedJobPostings()
    await getJobmatchProfile()
  }

  const initTabs = () => {
    tabs.value = [
      {
        id: 1,
        name: appStore.content.jobMatchPage?.resultPage?.tabTitleAllJobs,
        href: '#',
        icon: 'calendar'
      },
      {
        id: 2,
        name: appStore.content.jobMatchPage?.resultPage?.tabTitleSavedJobs,
        href: '#',
        icon: 'bookmark'
      }
    ] as ITab[]

    activeTab.value = tabs.value[0]

    savedPostingsTabName()
    setActiveTab(getLocalStorageTab(appStore.content?.title, tabs.value))
  }

  const fetchMore = async () => {
    if (canFetchMore()) {
      isFetching.value = true

      if (jobmatchParams.value.offset !== undefined) {
        jobmatchParams.value.offset += 50
      }
      const jobMatchApi = await factory.getJobMatchApi()

      const response = await jobMatchApi.getMatchesForProfile({
        jobMatchSearchFilters: jobmatchParams.value
      })

      if (response.results) {
        jobPostings.value.push(...response.results)
      }

      isFetching.value = false
    }
  }

  const canFetchMore = () => {
    return (
      jobmatchParams.value.offset! + jobmatchParams.value.limit! < totalResults.value &&
      isFetching.value !== true
    )
  }

  const startSearch = async (params?: JobMatchSearchFilters) => {
    isSearching.value = true
    const jobMatchApi = await factory.getJobMatchApi()
    const response = await jobMatchApi.getMatchesForProfile({
      jobMatchSearchFilters: params !== undefined ? params : jobmatchParams.value
    })
    jobmatchParams.value.offset = 0

    jobPostings.value = response.results ?? []
    totalResults.value = response.totalResults ?? 0

    if (window.innerWidth > 767) {
      setSelectedJobPosting(jobPostings.value[0])
    }

    isSearching.value = false
  }

  const getJobPostingById = async (jobPostingId: number | undefined) => {
    if (jobPostingId === undefined) {
      return
    }

    isSearching.value = true

    try {
      const jobMatchApi = await factory.getJobMatchApi()
      const response = await jobMatchApi.getAdvert({ jobId: jobPostingId })
      if (response !== undefined) {
        selectedJobPosting.value = response
        totalResults.value = 1
      }
    } catch (error) {
      console.log(error)
    } finally {
      isSearching.value = false
    }
  }

  const getJobmatchProfile = async () => {
    try {
      const lang = localStorage.getItem('appLanguageLocale')
        ? (localStorage.getItem('appLanguageLocale')?.slice(0, 2) as EscoLanguage)
        : EscoLanguage.En
      const jobMatchApi = await factory.getJobMatchApi()
      const data = await jobMatchApi.getJobMatchProfile({ language: lang })

      jobmatchProfile.value = data
      occupationStore.setSelectedOccupation(data.selectedOccupations)
      geoSettings.value = data.geoSettings
      notificationSettings.value = data.notificationSettings

      currentJobDescription.value = data.currentJobDescription ?? ''

      if (jobFilters.value.length > 0) {
        jobFilters.value[0].selected = [data.profileFilters?.dateFilter ?? '']

        jobFilters.value[1].selected = data.profileFilters?.careerFilter ?? []

        jobFilters.value[2].selected = data.profileFilters?.industryFilter ?? []
      }
    } catch (error) {
      console.log(error)
    }
  }

  const updateJobmatchProfile = async () => {
    if (jobmatchProfile.value === undefined) {
      return
    }

    jobmatchProfile.value.currentJobDescription = currentJobDescription.value

    if (
      geoSettings.value !== undefined &&
      geoSettings.value.latitude !== 0 &&
      geoSettings.value.longitude !== 0
    ) {
      jobmatchProfile.value.geoSettings = geoSettings.value
    }

    if (jobFilters.value.length > 0) {
      jobmatchProfile.value.profileFilters = {
        dateFilter: jobFilters.value[0].selected[0] as JobMatchDateFilter,
        careerFilter: jobFilters.value[1].selected,
        industryFilter: jobFilters.value[2].selected
      }
    }

    if (notificationSettings.value !== undefined) {
      jobmatchProfile.value.notificationSettings = notificationSettings.value
    }
    const jobMatchApi = await factory.getJobMatchApi()
    await jobMatchApi.updateJobMatchProfile({ jobMatchProfile: jobmatchProfile.value })
    await getJobMatchCount()
  }

  const getJobFilters = async () => {
    try {
      gettingFilters.value = true
      const language = localStorage.getItem('appLanguageLocale')
      // get the locale, to make sure its correct format for getting filters in backend
      const locale = getLocaleBasedOnDeviceLocale(language || 'da')
      const jobSearchApiV2 = await factory.getJobSearchApiV2()
      const response = await jobSearchApiV2.getJobFilters({ language: locale ?? 'da-DK' })

      const mapFilters = (filters: TranslatedSearchFilter[] | undefined): IFilterOption[] =>
        filters?.map(
          (filter: TranslatedSearchFilter) =>
            ({
              value: filter.id,
              name: filter.name
            }) as IFilterOption
        ) ?? []

      if (response != undefined) {
        jobFilters.value = [
          {
            id: 'datesFilters',
            name: localizationStore.t('general_Posted_Date'),
            options: dates.map((date) => ({
              value: date.id,
              name: localizationStore.t(date.translationKey)
            })),
            selected: [dates[2].id],
            hide: false,
            type: FilterItemType.Select
          },
          {
            id: 'careerFilters',
            name: localizationStore.t('general_Career_Level'),
            options: sortArrayAlphabetically(mapFilters(response.careerFilters), 'name'),
            selected: [],
            hide: false,
            type: FilterItemType.Checkbox
          },
          {
            id: 'industryFilters',
            name: localizationStore.t('general_Industry'),
            options: sortArrayAlphabetically(mapFilters(response.industryFilters), 'name'),
            selected: [],
            hide: false,
            type: FilterItemType.Checkbox
          }
        ]
      }

      gettingFilters.value = false
    } catch (error) {
      console.log(error)
    }
  }

  const getSavedJobPostings = async () => {
    try {
      const jobMatchApi = await factory.getJobMatchApi()
      const response = await jobMatchApi.getSavedAdverts()
      savedJobPostings.value = response ?? []
    } catch (error) {
      console.log(error)
    }
  }

  const createSavedJobPosting = async (jobId: number | string | undefined) => {
    if (jobId === undefined) {
      return
    }
    const jobMatchApi = await factory.getJobMatchApi()
    const response = await jobMatchApi.createSavedAdvert({ body: +jobId })

    if (response !== undefined) {
      jobPostings.value.find((jobPosting: JobResultItem) => {
        if (jobPosting.jobId === jobId) {
          jobPosting.isSavedAdvert = true
        }
      })
      if (selectedJobPosting.value?.jobId === jobId) {
        selectedJobPosting.value.isSavedAdvert = true
      }
      savedJobPostings.value.unshift(response)
    }
    savedPostingsTabName()
  }

  const deleteSavedJobPosting = async (jobId: number | string | undefined) => {
    if (jobId === undefined) {
      return
    }
    const jobMatchApi = await factory.getJobMatchApi()
    const response = await jobMatchApi.deleteSavedAdvert({ body: +jobId })

    if (response === true) {
      const index = savedJobPostings.value.findIndex(
        (jobPosting: JobResultItem) => jobPosting.jobId === jobId.toString()
      )
      savedJobPostings.value.splice(index, 1)
      if (selectedJobPosting.value?.jobId === jobId) {
        selectedJobPosting.value.isSavedAdvert = false
      }
      jobPostings.value.find((jobPosting: JobResultItem) => {
        if (jobPosting.jobId === jobId) {
          jobPosting.isSavedAdvert = false
        }
      })

      if (activeTab.value?.id === 2) {
        selectedJobPosting.value = savedJobPostings.value[0]
      }
    }
    savedPostingsTabName()
  }

  const setSelectedJobPosting = (jobPosting: JobResultItem | undefined) => {
    selectedJobPosting.value = jobPosting
  }

  const setGeoSettings = () => {
    const { selectedLocation, selectedRadius, selectedCountry } = locationStore

    if (selectedLocation === undefined) {
      return undefined
    }

    const { postal, city, lon, lat } = selectedLocation

    geoSettings.value = {
      zipCode: postal,
      city: city,
      radiusKm: selectedRadius.id !== undefined ? +selectedRadius?.id : 50,
      longitude: lon,
      latitude: lat,
      country: selectedCountry.id?.toString()
    }
  }

  const emptyProfileLocationData = async () => {
    if (jobmatchProfile.value === undefined) {
      return
    }

    jobmatchProfile.value.geoSettings = {
      latitude: 0,
      longitude: 0,
      zipCode: '',
      city: '',
      radiusKm: 0,
      country: locationStore.selectedCountry.id?.toString()
    }
    geoSettings.value = jobmatchProfile.value.geoSettings
    const jobMatchApi = await factory.getJobMatchApi()
    await jobMatchApi.updateJobMatchProfile({ jobMatchProfile: jobmatchProfile.value })
  }

  const resetFilters = async () => {
    const canReset = jobFilters.value.find(
      (filter: IFilterItem) => filter.id !== 'datesFilters' && filter.selected.length > 0
    )

    if (canReset !== undefined) {
      jobFilters.value.forEach((filter: IFilterItem) => {
        if (filter.id !== 'datesFilters') {
          filter.selected = []
        }
      })

      await updateAndSearch()
    }
  }

  const setActiveTab = (tab: ITab) => {
    activeTab.value = tab

    if (tab.id === 1) {
      selectedJobPosting.value = jobPostings.value[0]
    } else {
      selectedJobPosting.value = savedJobPostings.value[0]
    }

    setLocalStorageTab(tab.id, appStore.content?.title)
  }

  const updateAndSearch = async () => {
    await updateJobmatchProfile()
    await startSearch()
  }

  const savedPostingsTabName = () => {
    tabs.value[1].name =
      savedJobPostings.value?.length > 0
        ? `${appStore.content.jobMatchPage?.resultPage?.tabTitleSavedJobs} (${savedJobPostings.value.length})`
        : appStore.content.jobMatchPage?.resultPage?.tabTitleSavedJobs
  }

  const navigateToJobApplication = () => {
    const jobId = selectedJobPosting.value?.jobId

    if (jobId !== undefined) {
      const newUrl = `${appStore.content.jobMatchPage.resultPage.navigateToJobApplication}?jobid=${jobId}`
      router.push(newUrl)
    }
  }

  const getJobMatchCount = async () => {
    try {
      const jobMatchApi = await factory.getJobMatchApi()
      const response: AdvertCountResult = await jobMatchApi.getJobMatchCount()

      if (response !== undefined) {
        jobmatchCount.value = response
      }
    } catch (error) {
      console.log(error)
    }
  }

  const setBlockItemJobsFound = (value: number) => {
    blockItemJobsFound.value = value
  }

  const getPositionAndLocation = async () => {
    // Get GeoSettings from JobMatch Profile, if geosettings is empty, we check the user profile for location data.
    // If the profile is empty, we get the default values from brandmappings
    const { latitude, longitude, city, zipCode } = geoSettings.value || {}

    if (occupationStore.selectedOccupations.length <= 0) {
      await locationStore.getLocation()

      setGeoSettings()

      await updateJobmatchProfile()
    } else {
      locationStore.setSelectedLocation({
        city: city,
        postal: zipCode,
        lat: latitude,
        lon: longitude
      })
    }
  }

  const setNotificationSettings = (notifiSettings: JobMatchNotificationSettings) => {
    if (notificationSettings !== undefined) {
      notificationSettings.value = notifiSettings
    }
  }

  const createOrDeleteJobPosting = (jobPosting: JobResultItem | undefined) => {
    if (jobPosting === undefined) {
      return
    }

    if (jobPosting.isSavedAdvert === true) {
      deleteSavedJobPosting(jobPosting.jobId)
    } else {
      createSavedJobPosting(jobPosting.jobId)
    }
  }

  const canContinueToSearch = () => {
    if (wizardStore.currentStep()?.type === 'wizardStepJobMatchEmailNotification') {
      return occupationStore.selectedOccupations.length > 0
    }
    return true
  }

  const updateFilters = async (id: string, selected: string[]) => {
    jobFilters.value = jobFilters.value.map((filter) =>
      filter.id === id ? { ...filter, selected: selected } : filter
    )
  }

  const resetJobmatchStore = () => {
    initialized.value = false
  }

  return {
    jobmatchProfile,
    geoSettings,
    jobPostings,
    selectedJobPosting,
    totalResults,
    isSearching,
    jobmatchCount,
    blockItemJobsFound,
    isFetching,
    fetchMore,
    jobFilters,
    gettingFilters,
    savedJobPostings,
    tabs,
    activeTab,
    notificationSettings,
    init,
    startSearch,
    setSelectedJobPosting,
    getJobmatchProfile,
    updateJobmatchProfile,
    emptyProfileLocationData,
    navigateToJobApplication,
    getJobMatchCount,
    setBlockItemJobsFound,
    resetFilters,
    updateAndSearch,
    setActiveTab,
    createSavedJobPosting,
    deleteSavedJobPosting,
    setGeoSettings,
    getJobPostingById,
    createOrDeleteJobPosting,
    initialized,
    setNotificationSettings,
    canFetchMore,
    currentJobDescription,
    savedPostingsTabName,
    canContinueToSearch,
    initTabs,
    resultPageInit,
    updateFilters,
    resetJobmatchStore
  }
})
